import {
  Timestamp,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { db } from '@/lib/firebaseConfig';
import {
  FILES_SUBCOLLECTION,
  FILE_EXPIRY_MS,
  FILE_PURGE_AFTER_DELETE_MS,
  USERS_COLLECTION,
} from '../constants';
import type { CreateFileMetadataInput, FileMetadata } from '../types';

const filesCol = (uid: string) =>
  collection(db, USERS_COLLECTION, uid, FILES_SUBCOLLECTION);

export async function createFileMetadata(
  input: CreateFileMetadataInput
): Promise<void> {
  await setDoc(
    doc(filesCol(input.uid), input.fileId),
    {
      fileName: input.fileName,
      storagePath: input.storagePath,
      size: input.size,
      mimeType: input.mimeType || '',
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromMillis(input.createdAtMs + FILE_EXPIRY_MS),
      deletedAt: input.hidden ? serverTimestamp() : null,
      purgeAt: input.hidden
        ? Timestamp.fromMillis(input.createdAtMs + FILE_PURGE_AFTER_DELETE_MS)
        : null,
      ...(input.hidden ? { hidden: true } : {}),
      ...(input.passwordProtected !== undefined
        ? { passwordProtected: input.passwordProtected }
        : {}),
    },
    { merge: true }
  );
}

export async function setFilePasswordProtected(
  uid: string,
  fileId: string,
  passwordProtected: boolean
): Promise<void> {
  await setDoc(
    doc(filesCol(uid), fileId),
    { passwordProtected },
    { merge: true }
  );
}

export async function listActiveFiles(uid: string): Promise<FileMetadata[]> {
  const q = query(filesCol(uid), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  const now = Date.now();

  // Expiry is a client-side rule, so pagination must receive the complete
  // filtered list instead of filtering independent Firestore cursor windows.
  return snap.docs
    .map(
      (item) =>
        ({
          fileId: item.id,
          ...(item.data() as Omit<FileMetadata, 'fileId'>),
        }) as FileMetadata
    )
    .filter((item) => isFileAccessible(item, now));
}

export async function getFileMetadata(
  uid: string,
  fileId: string
): Promise<FileMetadata | null> {
  const snap = await getDoc(doc(filesCol(uid), fileId));
  return snap.exists()
    ? { fileId: snap.id, ...(snap.data() as Omit<FileMetadata, 'fileId'>) }
    : null;
}

export async function softDeleteFile(
  uid: string,
  fileId: string
): Promise<void> {
  await setDoc(
    doc(filesCol(uid), fileId),
    {
      deletedAt: serverTimestamp(),
      purgeAt: Timestamp.fromMillis(Date.now() + FILE_PURGE_AFTER_DELETE_MS),
    },
    { merge: true }
  );
}

export async function renameFileMetadata(
  uid: string,
  fileId: string,
  fileName: string
): Promise<void> {
  await setDoc(doc(filesCol(uid), fileId), { fileName }, { merge: true });
}

export function isFileAccessible(
  f: Pick<FileMetadata, 'deletedAt' | 'expiresAt'>,
  now: number = Date.now()
): boolean {
  if (f.deletedAt) return false;
  const exp = f.expiresAt?.toMillis?.() ?? null;
  return exp === null || exp > now;
}
